<script setup>
import { ref, computed, watch, inject, shallowRef, onMounted, onUnmounted } from 'vue'
import { useRequest } from 'composables/useRequest'
import { useFormatting } from 'composables/useFormatting'
import { useConstants } from 'composables/useConstants'
import TimezoneSelector from './TimezoneSelector.vue'
import TimeSlots from './TimeSlots.vue'

const emit = defineEmits(['slot-confirmed'])

const { axiosRequest } = useRequest()
const { DaysFromNowOn, strDatetoObject, dateObjectToFormattedStr } = useFormatting()
const { I18N, CURRENT_LOCALE, UI_LOCALE_NAME } = useConstants()

const event     = inject('event')
const attendee  = inject('attendee')
const timezones = inject('timezones')
const userUrl   = inject('userUrl')
const eventUrl  = inject('eventUrl')

// Calendar state
const date            = ref('')
const selectedSlot    = ref(null)
const calendarPage    = ref({ month: new Date().getMonth() + 1, year: new Date().getFullYear() })
// Slots go stale the moment somebody else books, so a month is only trusted for
// SLOTS_TTL_MS. Without this the picker keeps offering a slot that was taken
// after the page loaded, and the booking is rejected with "Slot is already booked".
const SLOTS_TTL_MS    = 60 * 1000
const slotsCache      = shallowRef({})   // { monthKey: { fetchedAt, days: { 'dd-mm-yyyy': [...slots] } } }
const fetchingMonths  = ref(new Set())
const loaderVisible   = ref(false)
const is24Hour        = ref(attendee.value.time_format === '24_hours')
const isDark          = ref(document.documentElement.classList.contains('dark'))

let _darkObserver = null
onMounted(() => {
  _darkObserver = new MutationObserver(() => {
    isDark.value = document.documentElement.classList.contains('dark')
  })
  _darkObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
})
onUnmounted(() => _darkObserver?.disconnect())

// 24h toggle keeps attendee.time_format in sync
watch(is24Hour, val => {
  attendee.value.time_format = val ? '24_hours' : '12_hours'
})

// Refetch when timezone changes (clear cache)
watch(() => attendee.value.timezone, () => {
  slotsCache.value = {}
  fetchingMonths.value.clear()
  if (date.value) fetchMonth()
  else fetchMonth()   // prefetch current calendar page
})

// Fetch slots when date is picked
watch(date, (newVal, oldVal) => {
  if (newVal == null) { date.value = oldVal; return }
  selectedSlot.value = null
  fetchMonth()
})

// Refresh whenever the tab regains focus — the common case is a booker who left
// the page open, came back, and picked a slot that has since been taken.
function refreshOnFocus() {
  if (document.visibilityState === 'visible') fetchMonth(true)
}
onMounted(() => document.addEventListener('visibilitychange', refreshOnFocus))
onUnmounted(() => document.removeEventListener('visibilitychange', refreshOnFocus))

// ── helpers ──────────────────────────────────────────────────────────────────

function currentMonthName() {
  const d = new Date(calendarPage.value.year, calendarPage.value.month - 1, 1)
  const month = d.toLocaleString('en-US', { month: 'long' }).toLowerCase()
  return `${month}-${calendarPage.value.year}`
}

function currentMonthStartStr() {
  return `1-${calendarPage.value.month}-${calendarPage.value.year}`
}

function lastDayOfCurrentPage() {
  return new Date(calendarPage.value.year, calendarPage.value.month, 0).getDate()
}

function releaseMonth(monthKey) {
  const next = new Set(fetchingMonths.value)
  next.delete(monthKey)
  fetchingMonths.value = next
}

async function fetchMonth(force = false) {
  const monthKey = currentMonthName()
  if (fetchingMonths.value.has(monthKey)) return

  const cached = slotsCache.value[monthKey]
  if (cached && !force && Date.now() - cached.fetchedAt < SLOTS_TTL_MS) return

  const tz = attendee.value.timezone
  if (!tz) return

  fetchingMonths.value = new Set([...fetchingMonths.value, monthKey])
  // A refresh of a month we can already render stays silent, so the slot list
  // does not blink away under the booker every time it revalidates.
  loaderVisible.value = !cached

  // Encode dots in timezone names — Rails routing interprets dots as format extensions
  const tzName = tz.name.replace(/\./g, '__dot__')

  const endpoint = `/api/${userUrl}/${eventUrl}/availabilities/${currentMonthStartStr()}/timezone/${tzName}`

  try {
    const res = await axiosRequest('GET', endpoint, {}, false)

    if (!res.js_error && res.response.success) {
      const incoming = res.response.data.available_slots
      const apiKey   = Object.keys(incoming)[0]
      slotsCache.value = {
        ...slotsCache.value,
        [monthKey]: { fetchedAt: Date.now(), days: incoming[apiKey] ?? {} },
      }
      dropSelectedSlotIfGone(monthKey)
    }
  } finally {
    // Always released — a month left in the in-flight set could never be
    // retried, so one failed request used to blank the calendar permanently.
    releaseMonth(monthKey)
    loaderVisible.value = false
  }
}

// The slot the booker had highlighted may have just been taken by someone else.
// Only acts on the month that was actually refreshed, so a background fetch of
// a different page cannot clear a valid selection.
function dropSelectedSlotIfGone(monthKey) {
  if (!selectedSlot.value || !date.value) return
  if (monthKey !== selectedDateMonthKey()) return

  const stillThere = slotsForDate.value.some(
    s => s.utc_minutes?.start_at === selectedSlot.value.utc_minutes?.start_at
  )
  if (!stillThere) selectedSlot.value = null
}

// Called by the parent when the server rejects a booking as already taken.
function refreshSlots() {
  return fetchMonth(true)
}

defineExpose({ refreshSlots })

// ── computed calendar helpers ─────────────────────────────────────────────────

const availableDays = computed(() => {
  const monthData = slotsCache.value[currentMonthName()]?.days
  if (!monthData) return []
  return Object.keys(monthData).map(d => strDatetoObject(d))
})

const notAvailableDays = computed(() => {
  const year  = calendarPage.value.year
  const month = calendarPage.value.month
  const last  = new Date(year, month, 0).getDate()
  const all   = Array.from({ length: last }, (_, i) => {
    return dateObjectToFormattedStr(new Date(year, month - 1, i + 1))
  })
  const avb = availableDays.value.map(d => dateObjectToFormattedStr(d))
  return all.filter(d => !avb.includes(d)).map(d => strDatetoObject(d))
})

const calendarAttrs = computed(() => [
  { key: 'today', dot: 'gray', dates: new Date() },
  { highlight: { color: 'blue', fillMode: 'light' }, dates: availableDays.value },
])

// Slots for the currently selected date
const selectedDateStr = computed(() =>
  date.value ? dateObjectToFormattedStr(date.value instanceof Date ? date.value : new Date(date.value)) : ''
)

function selectedDateMonthKey() {
  if (!date.value) return null
  try {
    const d = date.value instanceof Date ? date.value : new Date(date.value)
    return `${d.toLocaleString('en-US', { month: 'long' }).toLowerCase()}-${d.getFullYear()}`
  } catch { return null }
}

const slotsForDate = computed(() => {
  const mn = selectedDateMonthKey()
  if (!mn) return []
  return slotsCache.value[mn]?.days?.[selectedDateStr.value] ?? []
})

const uiDate = computed(() => {
  if (!date.value) return ''
  const d = date.value instanceof Date ? date.value : new Date(date.value)
  return d.toLocaleDateString(UI_LOCALE_NAME, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
})

// ── actions ───────────────────────────────────────────────────────────────────

function handleCalendarPageChange(page) {
  calendarPage.value = page
  fetchMonth()
}

function handleSlotSelected(slot) {
  selectedSlot.value = slot
}

function handleSlotConfirmed(slot) {
  emit('slot-confirmed', slot)
}

// Kick off initial fetch on mount (shared via the timezone watcher on creation)
fetchMonth()
</script>

<template>
  <div class="w-full h-full md:h-[99.9%] flex overflow-hidden">

    <!-- ── Calendar + timezone ────────────────────────────────────────── -->
    <!-- Width mirrors the original: narrower container (md:w-10/12 lg:w-9/12) when no date
         selected keeps the calendar visually compact; 7/12 when date+slots are side-by-side -->
    <div id="timelydo_public_event_date_selection"
         :class="date
           ? 'hidden sm:flex sm:flex-col sm:w-7/12 pl-5'
           : 'flex flex-col w-full md:w-10/12 lg:w-9/12 mx-auto pl-5'"
         class="h-full overflow-y-auto scrollbar-style">

      <!-- Section header + 24h toggle -->
      <div class="flex items-center justify-between pt-5 pb-3 flex-shrink-0 pr-7">
        <p class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
          <span class="hidden md:inline">Select a day &amp; time</span>
          <span class="md:hidden">Select a day</span>
        </p>
        <label id="timelydo_public_event_is24hour_toggle"
               class="relative inline-flex items-center gap-2 cursor-pointer">
          <input type="checkbox" v-model="is24Hour" class="sr-only" />
          <div class="relative w-9 h-5 rounded-full transition-colors"
               :class="is24Hour ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'">
            <div class="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform"
                 :class="is24Hour ? 'translate-x-4' : 'translate-x-0'" />
          </div>
          <span class="text-[11px] font-semibold text-gray-400 dark:text-gray-500
                       uppercase tracking-wider whitespace-nowrap">24h</span>
        </label>
      </div>

      <!-- Calendar widget -->
      <div id="timelydo_public_event_calendar" class="relative flex-shrink-0">
        <v-date-picker
          :attributes="calendarAttrs"
          :locale="CURRENT_LOCALE"
          class="mt-5 -ml-3.5 public-calendar"
          :class="date ? 'public-calendar-date-selected' : 'public-calendar-date-unselected'"
          v-model="date"
          :is-dark="isDark"
          :min-date="new Date()"
          :max-date="DaysFromNowOn(85)"
          :disabled-dates="notAvailableDays"
          title-position="left"
          @update:from-page="handleCalendarPageChange"
        />
        <!-- Spinner overlay while fetching availability -->
        <div v-if="loaderVisible"
             class="-mt-80 bg-gray-50/70 h-[315px] -ml-3.5 mr-4 relative z-[9999] mb-8
                    custom-loader dark:bg-gray-600/80" />
      </div>

      <!-- Timezone selector -->
      <div id="timelydo_public_event_timezone_selector" class="pb-5 mt-1 flex-shrink-0 pr-8">
        <p class="text-[11px] font-semibold text-gray-500 dark:text-gray-400
                  uppercase tracking-wider mb-2">
          Timezone
        </p>
        <TimezoneSelector v-model="attendee.timezone" :timezones="timezones ?? []" />
      </div>
    </div>

    <!-- ── Time slots panel ── -->
    <div v-if="date"
         id="timelydo_public_event_slots_panel"
         class="flex flex-col w-full sm:w-5/12 h-full overflow-hidden">

      <!-- Date header -->
      <div id="timelydo_public_event_slots_date" class="px-4 pt-5 pb-3 flex-shrink-0">
        <p class="text-[11px] font-semibold text-gray-400 dark:text-gray-500
                  uppercase tracking-wider leading-tight">
          {{ uiDate }}
        </p>
      </div>

      <!-- Slot list -->
      <div id="timelydo_public_event_slots_list" class="flex-1 overflow-y-auto scrollbar-style px-3 py-3">
        <TimeSlots
          :slots="slotsForDate"
          :selected-slot="selectedSlot"
          :time-format="attendee.time_format"
          @slot-selected="handleSlotSelected"
          @slot-confirmed="handleSlotConfirmed"
        />
      </div>
    </div>

  </div>
</template>
